import { Injectable } from '@angular/core';
import { Posthog } from '@capawesome/capacitor-posthog';
import { Capacitor } from '@capacitor/core';
import posthog from 'posthog-js';

import { scrubPosthogEvent, scrubPosthogRecordedRequest, scrubPosthogText } from 'src/shared/util/posthog-scrub';

import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PosthogService {
  private isInitialized = false;
  private initRetryCount = 0;
  private readonly maxRetries = 5;
  private readonly retryDelay = 2000;
  private currentUserId: string | null = null;

  private flagsReadyResolve: (() => void) | null = null;
  private flagsReadyPromise: Promise<void> | null = null;
  private flagsLoaded = false;
  private static readonly FLAG_READY_TIMEOUT_MS = 3000;

  private identificationResolve: (() => void) | null = null;
  private identificationPromise: Promise<void>;
  private identificationComplete = false;
  private static readonly IDENTIFICATION_TIMEOUT_MS = 8000;

  constructor() {
    this.identificationPromise = new Promise<void>((resolve) => {
      this.identificationResolve = resolve;
    });
  }

  async setup(): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    if (!environment.posthog_enabled) {
      return false;
    }

    if (!environment.posthog_api_key) {
      return false;
    }

    this.flagsReadyPromise = new Promise<void>((resolve) => {
      this.flagsReadyResolve = resolve;
    });

    try {
      // On web, use the proxy host to avoid ad-blocker/ITP blocking.
      // On native (iOS/Android), use the direct PostHog host (native SDKs are not blocked).
      const host = Capacitor.getPlatform() === 'web'
        ? environment.posthog_web_host
        : environment.posthog_host;

      await Posthog.setup({
        apiKey: environment.posthog_api_key,
        host,
      });

      this.isInitialized = true;

      // Disable autocapture on web — it registers global click/mousedown/pointerdown
      // listeners that can interfere with Ionic modal event handling.
      // Events are captured explicitly via posthogService.capture() instead.
      // The scrub hooks must land before the initial $pageview, which init() defers to a timer.
      if (Capacitor.getPlatform() === 'web') {
        posthog.set_config({
          autocapture: false,
          capture_pageview: false,
          before_send: scrubPosthogEvent,
          session_recording: {
            ...posthog.config.session_recording,
            maskCapturedNetworkRequestFn: scrubPosthogRecordedRequest,
          },
        });
      }

      if (Capacitor.getPlatform() === 'web') {
        if (environment.environment !== 'production') {
          posthog.config.disable_session_recording = true;
        }
        posthog.onFeatureFlags(() => {
          this.flagsLoaded = true;
          this.flagsReadyResolve?.();
        });
      } else {
        setTimeout(() => {
          this.flagsLoaded = true;
          this.flagsReadyResolve?.();
        }, 500);
      }

      // Safety timeout: resolve anyway if flags never load (network failure, blocked)
      setTimeout(() => {
        if (!this.flagsLoaded) {
          console.warn('PostHog: Flag readiness timeout - proceeding with cached/default flags');
          this.flagsReadyResolve?.();
        }
      }, PosthogService.FLAG_READY_TIMEOUT_MS);

      return true;
    } catch (error) {
      console.error('PostHog: Initialization error:', error);
      this.flagsReadyResolve?.();
      if (this.initRetryCount < this.maxRetries) {
        this.initRetryCount++;
        await new Promise(resolve => setTimeout(resolve, this.retryDelay));
        return this.setup();
      }
      return false;
    }
  }

  async waitForFlags(): Promise<void> {
    if (this.flagsLoaded) {return;}
    if (!this.isInitialized) {
      const initialized = await this.setup();
      if (!initialized) {return;}
    }
    if (this.flagsReadyPromise) {
      await this.flagsReadyPromise;
    }
  }

  async identify(userId: number, email: string, properties?: Record<string, any>): Promise<void> {
    // Ensure PostHog is initialized before attempting to identify
    if (!this.isInitialized) {
      await this.setup();
    }

    if (!this.isInitialized) {
      console.warn('PostHog: Cannot identify - initialization failed');
      return;
    }

    if (this.currentUserId === String(userId)) {
      // Same user — update properties (email may have changed, e.g. guest→real conversion)
      await this.setUserProperties({ email, environment: environment.environment, ...properties });
      return;
    }

    try {
      // Set person properties BEFORE identify() so that the /decide request
      // triggered internally by posthog.identify() already includes them.
      // Without this, the first /decide evaluates flags without properties
      // needed for experiment targeting (e.g. account_type), returning null.
      if (Capacitor.getPlatform() === 'web') {
        posthog.setPersonPropertiesForFlags({
          environment: environment.environment,
          ...properties
        });
      }

      await Posthog.identify({
        distinctId: String(userId),
        userProperties: {
          email,
          environment: environment.environment,
          ...properties
        }
      });
      this.currentUserId = String(userId);
      console.warn('PostHog: User identified:', userId);
    } catch (error) {
      console.error('PostHog: Error identifying user:', error);
    }
  }

  async capture(event: string, properties?: Record<string, any>): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      await Posthog.capture({
        event,
        properties: {
          platform: Capacitor.getPlatform(),
          isNative: Capacitor.isNativePlatform(),
          ...properties
        }
      });
    } catch (error) {
      console.error('PostHog: Error capturing event:', error);
    }
  }

  async screen(screenTitle: string, properties?: Record<string, any>): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    // Screen names are route URLs; native SDKs have no before_send hook to scrub them later.
    screenTitle = scrubPosthogText(screenTitle);
    try {
      if (Capacitor.getPlatform() === 'web') {
        // The Capacitor plugin throws 'Not implemented on web' for screen().
        // Fall back to a capture event that PostHog recognizes as a screen view.
        await this.capture('$screen', {
          $screen_name: screenTitle,
          ...properties,
        });
      } else {
        await Posthog.screen({
          screenTitle,
          properties: {
            platform: Capacitor.getPlatform(),
            ...properties
          }
        });
      }
    } catch (error) {
      console.error('PostHog: Error tracking screen:', error);
    }
  }

  async reset(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      await Posthog.reset();
      this.currentUserId = null;
      this.flagsLoaded = false;
      this.flagsReadyPromise = new Promise<void>((resolve) => {
        this.flagsReadyResolve = resolve;
      });
      // Reset generates a fresh (anonymous) distinct_id and reloads flags for it. If that
      // reload never lands (blocked/offline), don't leave an awaited waitForFlags() hanging
      // — proceed with cached/default flags after the same timeout used elsewhere.
      setTimeout(() => {
        if (!this.flagsLoaded) {
          console.warn('PostHog: Flag reload timeout after reset - proceeding with cached flags');
          this.flagsReadyResolve?.();
        }
      }, PosthogService.FLAG_READY_TIMEOUT_MS);
      console.warn('PostHog: User reset');
    } catch (error) {
      console.error('PostHog: Error resetting:', error);
    }
  }

  /**
   * Whether PostHog currently holds an identified (non-anonymous) distinct_id. Web-first
   * (the PM demo is a web experience); on native we fall back to our own tracked id.
   */
  isIdentified(): boolean {
    if (!this.isInitialized) {
      return false;
    }
    if (Capacitor.getPlatform() !== 'web') {
      return !!this.currentUserId;
    }
    try {
      if (typeof (posthog as any)._isIdentified === 'function') {
        return (posthog as any)._isIdentified();
      }
    } catch {
      /* fall through to the distinct_id-shape heuristic */
    }
    // Fallback: we always identify() with a numeric customer id, while anonymous
    // distinct_ids are UUIDs — so an all-digits id means the session is identified.
    try {
      const distinctId = posthog.get_distinct_id();
      return typeof distinctId === 'string' && /^\d+$/.test(distinctId);
    } catch {
      return !!this.currentUserId;
    }
  }

  /**
   * Force the session back to a fresh anonymous distinct_id IF it is currently identified.
   * Called at demo entry: a browser can still carry a prior identified real-account identity
   * (a previous demo→real conversion, or a real session whose token expired without an
   * explicit logout/reset). If the demo experiment exposures fire while identified, they
   * attach to that stale person, and the later demo→real identify() can't merge
   * identified→identified — so the exposure and the conversion land on different people and
   * the experiment never attributes the conversion. Resetting only when identified leaves a
   * genuinely anonymous visitor (and their sticky bucketing) untouched.
   */
  async resetIfIdentified(): Promise<void> {
    if (this.isIdentified()) {
      await this.reset();
    }
  }

  async setUserProperties(properties: Record<string, any>): Promise<void> {
    if (!this.isInitialized || !this.currentUserId) {
      return;
    }

    try {
      await Posthog.identify({
        distinctId: this.currentUserId,
        userProperties: properties
      });
    } catch (error) {
      console.error('PostHog: Error setting user properties:', error);
    }
  }

  async getFeatureFlag(key: string): Promise<string | boolean | null> {
    if (!this.isInitialized) {
      return null;
    }

    // On native, wait for the full identify → reloadFeatureFlags cycle to finish
    // before reading flags. The native PostHog SDK crashes if getFeatureFlag()
    // is called while identify() or reloadFeatureFlags() are still running internally.
    if (Capacitor.isNativePlatform()) {
      await this.waitForIdentification();
      await this.waitForFlags();
    }

    try {
      const result = await Posthog.getFeatureFlag({ key });
      return result.value;
    } catch (error) {
      console.error('PostHog: Error getting feature flag:', error);
      return null;
    }
  }

  /**
   * Reads a feature flag WITHOUT emitting the `$feature_flag_called` exposure event,
   * so a variant can be evaluated/cached early (e.g. warmed when a modal opens) without
   * enrolling the user in the experiment. Record exposure explicitly via capture() when
   * the user actually qualifies. Web uses posthog-js `send_event: false`; native SDKs have
   * no equivalent toggle, so this defers to the normal read there (acceptable — the flows
   * that need this are web-first and drive enrollment from an explicit capture()).
   */
  async getFeatureFlagNoExposure(key: string): Promise<string | boolean | null> {
    if (!this.isInitialized) {
      return null;
    }
    if (Capacitor.getPlatform() === 'web') {
      try {
        const value = posthog.getFeatureFlag(key, { send_event: false });
        return value === undefined ? null : (value as string | boolean);
      } catch (error) {
        console.error('PostHog: Error reading feature flag (no exposure):', error);
        return null;
      }
    }
    return this.getFeatureFlag(key);
  }

  async isFeatureEnabled(key: string): Promise<boolean> {
    if (!this.isInitialized) {
      return false;
    }

    if (Capacitor.isNativePlatform()) {
      await this.waitForIdentification();
      await this.waitForFlags();
    }

    try {
      const result = await Posthog.isFeatureEnabled({ key });
      return result.enabled;
    } catch (error) {
      console.error('PostHog: Error checking feature enabled:', error);
      return false;
    }
  }

  async getFeatureFlagPayload(key: string): Promise<any> {
    if (!this.isInitialized) {
      return null;
    }

    if (Capacitor.isNativePlatform()) {
      await this.waitForIdentification();
      await this.waitForFlags();
    }

    try {
      const result = await Posthog.getFeatureFlagPayload({ key });
      return result.value;
    } catch (error) {
      console.error('PostHog: Error getting feature flag payload:', error);
      return null;
    }
  }

  async reloadFeatureFlags(): Promise<void> {
    if (!this.isInitialized) {return;}

    // Reset flag readiness so waitForFlags() will wait for the new flags
    this.flagsLoaded = false;
    this.flagsReadyPromise = new Promise<void>((resolve) => {
      this.flagsReadyResolve = resolve;
    });

    // Safety timeout for the reload
    setTimeout(() => {
      if (!this.flagsLoaded) {
        console.warn('PostHog: Flag reload timeout - proceeding with cached flags');
        this.flagsReadyResolve?.();
      }
    }, PosthogService.FLAG_READY_TIMEOUT_MS);

    try {
      await Posthog.reloadFeatureFlags();
      // On native, the await returning IS the completion signal — the native SDK
      // has finished reloading. Mark flags as ready immediately instead of waiting
      // for the safety timeout.
      if (Capacitor.isNativePlatform() && !this.flagsLoaded) {
        this.flagsLoaded = true;
        this.flagsReadyResolve?.();
      }
    } catch (error) {
      console.error('PostHog: Error reloading feature flags:', error);
      this.flagsReadyResolve?.();
    }
  }

  completeIdentification(): void {
    if (this.identificationComplete) {return;}
    this.identificationComplete = true;
    this.identificationResolve?.();
  }

  async waitForIdentification(): Promise<void> {
    if (this.identificationComplete) {return;}
    const timeout = new Promise<void>((resolve) =>
      setTimeout(() => {
        if (!this.identificationComplete) {
          console.warn('PostHog: Identification timeout - proceeding with current flags');
          this.completeIdentification();
        }
        resolve();
      }, PosthogService.IDENTIFICATION_TIMEOUT_MS)
    );
    await Promise.race([this.identificationPromise, timeout]);
  }

  isReady(): boolean {
    return this.isInitialized;
  }
}
